import React, { useState } from "react";
import { AlertTriangle, Phone, X, ShieldAlert } from "lucide-react";
import api from "@/lib/api";

export default function SOSButton({ bookingId, driverName }) {
  const [open, setOpen] = useState(false);
  const [alerted, setAlerted] = useState(false);

  const call911 = () => {
    window.location.href = "tel:911";
  };

  const callDispatch = () => {
    window.location.href = "tel:+18317477141";
  };

  const alertAdmin = async () => {
    try {
      await api.post("/tickets", {
        subject: `🚨 SOS from active booking ${bookingId}`,
        message: `Urgent — customer pressed SOS during active job. Driver: ${driverName || "unknown"}. Booking: ${bookingId}. Please contact immediately.`,
        booking_id: bookingId,
        priority: "high",
      });
      setAlerted(true);
    } catch {
      setAlerted(true); // still show confirmation — admin ticket can be sent from support drawer
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="sos-btn"
        className="inline-flex items-center gap-2 px-4 py-2 bg-asap-red text-white font-display uppercase text-sm border-2 border-asap-red shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all"
        aria-label="Emergency SOS"
      >
        <AlertTriangle size={16}/> SOS
      </button>

      {open && (
        <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="sos-modal" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="bg-white border-4 border-asap-red max-w-md w-full shadow-hard">
            <div className="p-5 border-b-4 border-asap-red bg-asap-red text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <ShieldAlert size={28}/>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold">Emergency</div>
                  <h3 className="font-display text-2xl uppercase">Get Help Now</h3>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="sos-close" className="p-1 border-2 border-white hover:bg-white/10"><X size={16}/></button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-slate-700 text-sm">If you're in danger, call 911 immediately. Below are your fast lanes:</p>

              <button
                onClick={call911}
                data-testid="sos-911"
                className="w-full p-4 bg-asap-red text-white text-left border-2 border-asap-red hover:bg-red-700 transition"
              >
                <div className="flex items-center gap-3">
                  <Phone size={22}/>
                  <div>
                    <div className="font-display text-2xl uppercase">Call 911</div>
                    <div className="text-xs opacity-80">Police · Fire · Medical</div>
                  </div>
                </div>
              </button>

              <button
                onClick={callDispatch}
                data-testid="sos-dispatch"
                className="w-full p-4 bg-asap-deep text-white text-left border-2 border-asap-deep hover:bg-asap-blue transition"
              >
                <div className="flex items-center gap-3">
                  <Phone size={22} className="text-asap-yellow"/>
                  <div>
                    <div className="font-display text-xl uppercase">Call ASAP Dispatch</div>
                    <div className="text-xs opacity-80">831-747-7141 · 24/7 support</div>
                  </div>
                </div>
              </button>

              <button
                onClick={alertAdmin}
                disabled={alerted}
                data-testid="sos-alert-admin"
                className={`w-full p-4 border-2 border-asap-deep text-left transition ${alerted ? "bg-emerald-50 border-emerald-500 text-emerald-800" : "bg-white text-asap-deep hover:bg-asap-yellow"}`}
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle size={22} className={alerted ? "text-emerald-600" : "text-asap-orange"}/>
                  <div>
                    <div className="font-display text-xl uppercase">{alerted ? "Admin Alerted" : "Silent Alert to Admin"}</div>
                    <div className="text-xs opacity-80">
                      {alerted ? "A high-priority support ticket was opened. Someone will reach out immediately." : "Files a high-priority ticket without dialing anyone."}
                    </div>
                  </div>
                </div>
              </button>

              <p className="text-[11px] text-slate-500 mt-4">Your safety is our top priority. Stay on the line with 911 first if you're in immediate danger — the app will keep working in the background.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
